import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import styled from 'styled-components'
import arraySort from 'array-sort'
import update from 'immutability-helper'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import withApiData from '../../HOC/withApiData'
import H5 from '../../typography/headers/H5'
import P2 from '../../typography/paragraphs/P2'
import EditOrDeleteMenuButton from '../../mini/Buttons/EditOrDeleteMenuButton'
import ProgressBar from '../../mini/ProgressBar'
import colors from '../../../configuration/colors'
import Button from '../../mini/Buttons/Button'
import { showModal } from '../../../actions/modal'
import { deleteJourneyLevel, fetchJourneyLevel } from './API'
import _Clouds from '../../../static/images/masterylevel/clouds.svg'
import _Mountain from '../../../static/images/masterylevel/mountain.svg'
import { fetchLevelActivities } from '../Activities/API'
import ActivityRow from '../Activities/ActivityRow'
import MarkdownLabel from '../../mini/MarkdownLabel'
import { cleanHexCode } from '../util'
import { useIsLevelExpanded } from '../hooks/useIsExpanded'
import { setLevelIsExpanded } from '../../../actions/mycourses'

const LevelContainer = styled.div`
  margin: 11px 0;
  padding: 10px 22px;
  border-radius: 7px;
  border: 1px solid ${colors.ash};
  background-color: ${colors.white};
  cursor: pointer;
  box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.12);
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: center;
  align-items: center;
`

const LevelInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 90%;
  align-content: center;
  align-items: center;
`

const LevelIconContainer = styled.div`
  position: relative;
  width: 25px;
  height: 25px;
  box-shadow: 0 1px 2px 0 rgba(143, 143, 143, 0.35);
  border: 0.7px solid ${colors.ash};
  background-color: ${props => (props.isMastery ? colors.amber : colors.white)};
  border-radius: 3.5px;
  margin-right: 15px;
`

const LevelIconHeader = styled.div`
  position: absolute;
  border-top-left-radius: 2.5px;
  border-top-right-radius: 2.5px;
  width: 25px;
  height: 8px;
  background-color: ${props => props.color};
`

const LevelNumber = styled.div`
  position: absolute;
  padding-left: ${props => (props.isTwoDigitNumber ? '4px' : '8px')};
  padding-top: 5px;
`

const Clouds = styled.div`
  background-image: url(${_Clouds});
  height: 15px;
  width: 25px;
  position: absolute;
  z-index: 11;
  margin-top: 12px;
  border-bottom-left-radius: 2.8px;
  border-bottom-right-radius: 2.8px;
`

const Mountain = styled.div`
  background-image: url(${_Mountain});
  height: 25px;
  width: 30px;
  margin-right: 11px;
  position: absolute;
  z-index: 10;
  margin-left: -5px;
  border-bottom-left-radius: 11px;
`

const LevelRow = ({
  apiData,
  activityCourseId,
  isReadyForEnrollment,
  journeyId,
  level,
  color,
  levels,
  courseId,
  sequenceNumber
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const [detailedLevelInfo, setDetailedLevelInfo] = useState({})

  const shouldShowActivities = useIsLevelExpanded(level)

  const { data: levelData, refetch: fetchLevelDetails } = useQuery(
    ['journey-level-' + level.id],
    () => fetchJourneyLevel(apiData, activityCourseId, journeyId, level.id),
    {
      refetchOnWindowFocus: false,
      enabled: shouldShowActivities
    }
  )

  const { data: activities, refetch: fetchActivities } = useQuery(
    ['level-activities-' + level.id],
    () => fetchLevelActivities(apiData, level.id),
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: shouldShowActivities
    }
  )

  useEffect(() => {
    if (!!level.just_created) {
      dispatch(setLevelIsExpanded(level.id, true))
      fetchLevelDetails()
      fetchActivities()
    }
  }, [])

  useEffect(() => {
    if (!!level.just_created) {
      dispatch(setLevelIsExpanded(level.id, true))
    }
  }, [level.just_created])

  useEffect(() => {
    if (!!levelData && levelData !== detailedLevelInfo) {
      setDetailedLevelInfo(levelData)
    }
  }, [levelData])

  useEffect(() => {
    if (activitiesState !== activities) {
      setActivitiesState(arraySort(activities, 'sort_order'))
    }
  }, [activities])

  const [activitiesState, setActivitiesState] = useState(null)

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      const activitiesStateCopy = [...activitiesState]
      const dragCard = activitiesStateCopy[dragIndex]
      setActivitiesState(
        update(activitiesStateCopy, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard]
          ]
        })
      )
    },
    [activitiesState]
  )

  const handleActivityCreationModal = () => {
    dispatch(
      showModal('CREATE_ACTIVITY_MODAL', {
        activityCourseId,
        journeyId,
        levelId: level.id
      })
    )
  }

  const handleClickToDisplayActivityList = e => {
    const isShown = shouldShowActivities
    if (!isShown) {
      fetchLevelDetails()
      fetchActivities()
    }

    dispatch(setLevelIsExpanded(level.id, !isShown))
  }

  const deleteMutation = useMutation(
    ({ apiData, activityCourseId, journeyId, levelId }) =>
      deleteJourneyLevel(apiData, activityCourseId, journeyId, levelId)
  )

  const onDeleteClick = (apiData, activityCourseId, journeyId) => {
    deleteMutation.mutate(
      {
        apiData,
        activityCourseId,
        journeyId,
        levelId: level.id
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('journey-levels-' + journeyId)
        }
      }
    )
  }

  const handleChosenOption = option => {
    if (option === 'Delete') {
      if (!isReadyForEnrollment) {
        dispatch(
          showModal('CLOSE_MODAL', {
            description:
              'Are you sure you want to delete this level and all of its contents?',
            supportiveText: 'Once deleted, this cannot be undone.',
            isOptionalTextExist: false,
            deleteFunction: () =>
              onDeleteClick(apiData, activityCourseId, journeyId, level.id)
          })
        )
      } else {
        dispatch(
          showModal('INFORMATIVE_MODAL', {
            description: 'This level cannot be deleted.',
            supportiveText:
              'Once a journey is published, it’s levels cannot be deleted.'
          })
        )
      }
    }

    if (option === 'Edit Details') {
      fetchLevelDetails().then(levelInfo => {
        dispatch(
          showModal('CREATE_OR_EDIT_LEVEL_MODAL', {
            apiData,
            activityCourseId,
            journeyId: journeyId,
            isCreateLevel: false,
            level: level,
            description: levelInfo.data.description
          })
        )
      })
    }

    if (option === 'Sort Order') {
      dispatch(
        showModal('SORT_LEVEL_ORDER_MODAL', {
          apiData,
          activityCourseId,
          journeyId: journeyId,
          levels: levels
        })
      )
    }
  }

  const listOfActivities =
    activities !== undefined &&
    activitiesState !== null &&
    activitiesState.map((activity, index) => {
      return (
        <ActivityRow
          key={`activity-${activity.id}`}
          activity={activity}
          levelId={level.id}
          levels={levels}
          journeyId={journeyId}
          courseId={courseId}
          moveCard={moveCard}
          index={index}
          id={activity.id}
          isReadyForEnrollment={isReadyForEnrollment}
        />
      )
    })

  return (
    <LevelContainer
      id={`level-id-${level.id}`}
      onClick={e =>
        typeof e.target.className === 'string' &&
        (e.target.className.includes('LevelRow') ||
          e.target.className.includes('ac-level-name') ||
          e.target.className.includes('H5') ||
          e.target.className.includes('ProgressBar')) &&
        handleClickToDisplayActivityList()
      }
    >
      <LevelInfoContainer>
        <FlexContainer>
          <LevelIconContainer isMastery={level.mastery}>
            {level.mastery ? (
              <div>
                <Mountain />
                <Clouds />
              </div>
            ) : (
              <div>
                <LevelIconHeader color={cleanHexCode(color)} />
                <LevelNumber isTwoDigitNumber={sequenceNumber >= 10}>
                  {sequenceNumber}
                </LevelNumber>
              </div>
            )}
          </LevelIconContainer>
          <H5 style={{ flex: '30%' }}>
            <span className="ac-level-name">{level.name}</span>
          </H5>
        </FlexContainer>
        <FlexContainer>
          <H5 style={{ flex: '20%' }}>
            <span
              style={{ marginRight: '5px' }}
              className="ac-level-activities-quantity"
            >
              {level.quests_count || 0}
            </span>
            Activities
          </H5>
          <ProgressBar
            progress={
              level.quests_count > 0
                ? ((level.quests_count - level.quests_in_draft_count) /
                    level.quests_count) *
                  100
                : 0
            }
            progressBarWidth="108px"
          />
          <EditOrDeleteMenuButton
            options={
              isReadyForEnrollment
                ? ['Edit Details']
                : ['Edit Details', 'Delete', 'Sort Order']
            }
            handleChosenOption={handleChosenOption}
          />
        </FlexContainer>
      </LevelInfoContainer>
      {shouldShowActivities && (
        <div
          style={{
            marginTop: '5px',
            marginBottom: '15px'
          }}
        >
          {!!levelData && detailedLevelInfo !== {} && (
            <P2 style={{ maxWidth: '800px' }}>
              <MarkdownLabel value={detailedLevelInfo.description} />
            </P2>
          )}
          <DndProvider backend={HTML5Backend}>
            <div>{listOfActivities}</div>
          </DndProvider>
          {!isReadyForEnrollment && (
            <Button
              handleClick={handleActivityCreationModal}
              width="128px"
              title="+ Add Activity"
              buttonColor={colors.white}
              borderColor={colors.aluminum}
              hoverBackground={colors.charcoal4}
              hoverColor={colors.white}
            />
          )}
        </div>
      )}
    </LevelContainer>
  )
}

LevelRow.propTypes = {
  apiData: PropTypes.object.isRequired,
  activityCourseId: PropTypes.string.isRequired,
  courseId: PropTypes.string.isRequired,
  journeyId: PropTypes.number.isRequired,
  isReadyForEnrollment: PropTypes.bool.isRequired,
  level: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  levels: PropTypes.array.isRequired,
  sequenceNumber: PropTypes.number.isRequired
}

export default withApiData(LevelRow)



// WEBPACK FOOTER //
// ./src/components/mycourses/Levels/LevelRow.js